import { isPhrasingContent, isPresentationalPhrasingContent } from '../dom/content';
import { getComputedStyle, simplifyElement } from '../dom/element';
import { isHTMLOrSVGElement } from '../dom/node';
import { paintNodesInRange } from '../dom/paint';
import { getCascadingComputedStyle } from './computedStyle';

export function nodeHasStrikethrough(node: Node, container: Node): boolean {
  return cascadingComputedStyleHasStrikethrough(getCascadingComputedStyle(node, container));
}

export function cascadingComputedStyleHasStrikethrough(computedStyles: CSSStyleDeclaration[]): boolean {
  return computedStyles.some(styleHasStrikethrough);
}

export function styleHasStrikethrough(style: CSSStyleDeclaration | null): boolean {
  return !!style && textDecorationHasStrikethrough(style.textDecoration);
}

export function textDecorationHasStrikethrough(textDecoration: string | null): boolean {
  return !!textDecoration && !!textDecoration.match(/\bline-through\b/);
}

export function clearStrikethroughInElement(element: HTMLElement | SVGElement) {
  element.style.removeProperty('text-decoration-line');
  element.style.removeProperty('text-decoration');
}

export function clearStrikethroughInRange(container: Node, range: Range): Range {
  return paintNodesInRange(container, range, {
    shouldPaintNode: node => nodeHasStrikethrough(node, container),
    shouldSplitElement: element => isPresentationalPhrasingContent(element),
    paintElement: element => {
      let shouldSimplify = ['S', 'STRIKE', 'DEL'].includes(element.tagName);

      if (isHTMLOrSVGElement(element) && styleHasStrikethrough(getComputedStyle(element))) {
        shouldSimplify = shouldSimplify || element.tagName === 'SPAN';

        clearStrikethroughInElement(element);
      }

      if (shouldSimplify) {
        return simplifyElement(element);
      }

      return element;
    },
  });
}

export function setStrikethroughInElement(element: HTMLElement | SVGElement) {
  const parts = ['line-through'];

  const { textDecoration } = element.style;
  if (textDecoration) {
    parts.push(textDecoration);
  }

  // eslint-disable-next-line no-param-reassign
  element.style.textDecoration = parts.join(' ');
}

export function setStrikethroughInRange(container: Node, range: Range): Range {
  return paintNodesInRange(container, range, {
    shouldPaintNode: node => isPhrasingContent(node) && !nodeHasStrikethrough(node, container),
    createPaintedElement: document => document.createElement('s'),
    paintElement: element => {
      if (isHTMLOrSVGElement(element)) {
        setStrikethroughInElement(element);
      }

      return element;
    },
    forcePaintElement: element => {
      if (isHTMLOrSVGElement(element)) {
        // eslint-disable-next-line no-param-reassign
        element.style.textDecoration = 'line-through !important';
      }

      return element;
    },
  });
}
